import { Button, IconFont, Text } from '@byecode/ui'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { closestCenter, DndContext, MouseSensor, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import type { ArgumentsSchema } from '@lighthouse/core'
import { ANONYMOUS_PARAM_NAME, FlowNode } from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import { omit, reduce } from 'rambda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useImmer } from 'use-immer'

import { VariableItemControlled } from './VariableItemControlled'

interface VariableListControlledProps {
    allParentNodes?: FlowNode[]
    value?: (ArgumentsSchema & { valuePath?: string })[]
    onChange?: (next: (ArgumentsSchema & { valuePath?: string })[]) => void
    hasValuePath?: boolean
    hasValue?: boolean
}

const SCxContainer = styled.div`
    width: 100%;
    padding: 0 8px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
`

export const VariableListControlled: React.FC<VariableListControlledProps> = ({
    value,
    onChange,
    hasValuePath,
    hasValue,
    allParentNodes
}) => {
    const fields = useMemo(() => value || [], [value])
    const [activeId, setActiveId] = useState('')

    const [openedMap, setOpenMap] = useImmer<Record<string, boolean>>(() =>
        reduce<ArgumentsSchema, Record<string, boolean>>(
            (preVal, { id }) => {
                preVal[id] = true
                return preVal
            },
            {},
            fields
        )
    )

    useEffect(() => {
        setOpenMap(draft => {
            // remove missing ids
            Object.keys(draft).forEach(id => {
                if (!fields.some(f => f.id === id)) {
                    delete draft[id]
                }
            })
            // add new ids
            fields.forEach(f => {
                if (draft[f.id] === undefined) {
                    draft[f.id] = true
                }
            })
        })
    }, [fields, setOpenMap])

    const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 10 } }))

    const getInitParam = useCallback(() => {
        const id = nanoid(12)
        return {
            id,
            name: ANONYMOUS_PARAM_NAME,
            innerType: 'TEXT' as const,
            ...(hasValuePath ? { valuePath: '' } : {})
        }
    }, [hasValuePath])

    const handleDragStart = useCallback((event: DragStartEvent) => {
        const { active } = event
        setActiveId(active?.id.toString())
    }, [])

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event
            if (!over || active.id === over.id) {
                setActiveId('')
                return
            }
            const oldIndex = fields.findIndex(item => item.id === active.id)
            const newIndex = fields.findIndex(item => item.id === over.id)
            if (oldIndex < 0 || newIndex < 0) {
                setActiveId('')
                return
            }
            const next = [...fields]
            const [moved] = next.splice(oldIndex, 1)
            next.splice(newIndex, 0, moved)
            onChange?.(next)
            setActiveId('')
        },
        [fields, onChange]
    )

    return (
        <SCxContainer>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
                modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
            >
                <SortableContext items={fields.map(d => d.id)} strategy={verticalListSortingStrategy}>
                    {fields.map((item, index) => (
                        <VariableItemControlled
                            key={item.id}
                            item={item}
                            hasValuePath={hasValuePath}
                            hasValue={hasValue}
                            allParentNodes={allParentNodes}
                            opened={openedMap[item.id] && !activeId}
                            onChangeOpen={opened => {
                                setOpenMap(draft => {
                                    draft[item.id] = opened
                                })
                            }}
                            onDelete={() => {
                                const next = fields.filter((_, i) => i !== index)
                                onChange?.(next)
                                setOpenMap(draft => {
                                    draft = omit([item.id], draft)
                                })
                            }}
                            onChange={nextItem => {
                                const next = fields.map((f, i) => (i === index ? nextItem : f))
                                onChange?.(next)
                            }}
                        />
                    ))}
                </SortableContext>
                <Button
                    type="tag"
                    style={{ justifyContent: 'flex-start', borderRadius: 100, marginTop: 8 }}
                    onClick={() => {
                        const newItem = getInitParam()
                        setOpenMap(draft => {
                            draft[newItem.id] = true
                        })
                        onChange?.([...fields, newItem])
                    }}
                    icon={<IconFont size={16} color="var(--color-gray-500)" type="Add" />}
                >
                    <Text>添加</Text>
                </Button>
            </DndContext>
        </SCxContainer>
    )
}
